/**
 * 서비스 금액 관리 (USE_* 차감 트랜잭션 가격)
 *
 *  관리자가 사용하는 두 가지 액션:
 *   1) 금액 변경 — 새 값 + 시행일 + 사유 입력. 즉시 또는 예약 적용.
 *   2) 비활성 — 거의 안 쓰지만 미래 액션 가격 사전 정의 시 보류 처리.
 */
import { useEffect, useMemo, useState } from 'react';
import { Edit3, ChevronLeft, ChevronRight, Search, Calculator } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  creditApi,
  CreditPolicy,
  CreditPrice,
  CREDIT_TX_LABEL,
} from '@/services/api/credit';

const PAGE_SIZE = 10;

/** 원화 환산 정책으로 차감 크레딧을 산정하는 특수 액션 코드 */
const KRW_RATIO_TX_TYPE = 'USE_TICKET_OFFLINE';

/**
 * 실제 코드에 적용된 액션만 노출 (도입 시점에 화이트리스트 확장).
 *  - DB 에는 모든 시점 액션 정의가 남아있지만 사용자 혼동 방지를 위해 운영자 화면에선 숨김.
 *  - 신규 액션 적용 시 이 배열에 추가.
 */
const VISIBLE_TX_TYPES = new Set<string>([
  'USE_TICKET_OFFLINE',
  'USE_KAKAO_ALIM',        // 대체문자(USE_SMS_FALLBACK)는 이 안에서 함께 편집
  'USE_AI_DIARY_IMAGE',
  'USE_AI_DIARY_TEXT',
  'USE_AI_CONTRACT_IMPORT', // AI 계약서 문서 자동 변환 (Gemini)
]);

export function CreditPricesPage() {
  const [prices, setPrices] = useState<CreditPrice[]>([]);
  const [policies, setPolicies] = useState<CreditPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CreditPrice | null>(null);
  const [editingKrwRatio, setEditingKrwRatio] = useState<CreditPrice | null>(null);
  const [editingKakaoAlim, setEditingKakaoAlim] = useState<CreditPrice | null>(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    const [priceList, policyList] = await Promise.all([
      creditApi.listPrices(),
      creditApi.listPolicies().catch(() => [] as CreditPolicy[]),
    ]);
    setPrices(priceList);
    setPolicies(policyList);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  // 원화 환산 + 환원 정책 — 표시용 빠른 조회
  const krwRatioPolicies = useMemo(() => {
    const find = (k: string) => policies.find((p) => p.policyKey === k);
    return {
      ratio: find('MANUAL_TICKET_KRW_RATIO'),
      min: find('MANUAL_TICKET_MIN_CREDITS'),
      krwPerCredit: find('KRW_PER_CREDIT'),
      reversalRatio: find('MANUAL_TICKET_REVERSAL_RATIO'),
      reversalWindow: find('MANUAL_TICKET_REVERSAL_WINDOW_DAYS'),
      // 신규 FLOOR 정책 — 최소 금액 미만 시 일괄 부과
      krwFloor: find('MANUAL_TICKET_KRW_FLOOR'),
      floorCredits: find('MANUAL_TICKET_FLOOR_CREDITS'),
    };
  }, [policies]);

  // 알림톡 관련 액션 조회 (대체문자 단가는 이 안에서 함께 편집)
  const smsFallbackPrice = useMemo(
    () => prices.find((p) => p.cdTxType === 'USE_SMS_FALLBACK'),
    [prices]
  );

  // 검색 변경 시 1페이지로
  useEffect(() => { setPage(1); }, [q]);

  // 1차 — 화이트리스트 필터 (실제 코드에 적용된 액션만)
  const visiblePrices = prices.filter((p) => VISIBLE_TX_TYPES.has(p.cdTxType));

  // 2차 — 검색 필터 (액션명/코드)
  const qLower = q.trim().toLowerCase();
  const filtered = qLower
    ? visiblePrices.filter((p) =>
        p.cdTxType.toLowerCase().includes(qLower) ||
        (p.description ?? '').toLowerCase().includes(qLower) ||
        (CREDIT_TX_LABEL[p.cdTxType] ?? '').toLowerCase().includes(qLower)
      )
    : visiblePrices;

  // 페이징
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const pagedPrices = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">서비스 금액</h1>
        <p className="mt-1 text-sm text-gray-500">
          실제 코드에 적용된 액션만 노출됩니다. 신규 정책은 BE 적용 시점에 함께 등록됩니다.
        </p>
      </header>

      {/* 검색 + 총건수 */}
      <div className="flex items-center justify-between gap-3">
        <div className="relative w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="액션 코드 / 액션 명"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div className="text-xs text-gray-500">
          총 {filtered.length}개
          {filtered.length > PAGE_SIZE && (
            <span className="ml-1 text-gray-400">· {safePage}/{totalPages} 페이지</span>
          )}
        </div>
      </div>

      {/* 금액 테이블 — PaymentList 와 통일된 스타일 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 w-[260px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  액션 명
                </th>
                <th className="px-6 py-3 w-[220px] text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  액션 코드
                </th>
                <th className="px-6 py-3 w-[140px] text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  금액
                </th>
                <th className="px-6 py-3 w-[170px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  시행일
                </th>
                <th className="px-6 py-3 w-[100px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  상태
                </th>
                <th className="px-6 py-3 w-[110px] text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">로딩 중…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-gray-500">데이터 없음</td></tr>
              )}
              {!loading && pagedPrices.map((p) => {
                const displayName = p.description?.trim() || CREDIT_TX_LABEL[p.cdTxType] || p.cdTxType;
                const nowStr = new Date().toISOString();
                const notStarted = p.dtEffectiveFrom > nowStr;
                const expired = p.dtEffectiveTo && p.dtEffectiveTo <= nowStr;
                const status = notStarted ? '예정' : expired ? '만료' : '시행중';
                const statusCls = notStarted
                  ? 'bg-yellow-100 text-yellow-900'
                  : expired
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-green-100 text-green-800';
                const isKrwRatio = p.cdTxType === KRW_RATIO_TX_TYPE;
                return (
                  <tr key={p.priceSeq} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="font-medium text-gray-900 truncate" title={displayName}>
                        {displayName}
                        {isKrwRatio && (
                          <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 border border-sky-200">
                            <Calculator size={10} /> 원화 환산
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <code className="text-xs text-gray-500 font-mono">{p.cdTxType}</code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {isKrwRatio ? (
                        <div className="text-xs space-y-0.5">
                          <div className="text-sky-700">
                            차감 {krwRatioPolicies.ratio?.policyValue ?? '0.9'}%
                            <span className="text-gray-500"> · 최소 </span>
                            <span className="font-semibold">{krwRatioPolicies.min?.policyValue ?? '1'}크</span>
                          </div>
                          <div className="text-emerald-700 text-[11px]">
                            환원 {krwRatioPolicies.reversalRatio?.policyValue ?? '100'}%
                            <span className="text-gray-500"> · </span>
                            {krwRatioPolicies.reversalWindow?.policyValue ?? '30'}일내
                          </div>
                        </div>
                      ) : (
                        <>
                          <span className="font-bold text-gray-900 tabular-nums">{p.unitPrice}</span>
                          <span className="ml-1 text-xs text-gray-500">크레딧</span>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-xs text-gray-500">
                      {p.dtEffectiveFrom.slice(0, 10)}
                      {p.dtEffectiveTo && <div className="text-gray-400">~ {p.dtEffectiveTo.slice(0, 10)}</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusCls}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => {
                          if (isKrwRatio) setEditingKrwRatio(p);
                          else if (p.cdTxType === 'USE_KAKAO_ALIM') setEditingKakaoAlim(p);
                          else setEditing(p);
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary-600 hover:bg-primary-50"
                      >
                        <Edit3 size={12} />
                        변경
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 페이징 */}
      {!loading && filtered.length > PAGE_SIZE && (
        <PricePagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}

      {/* 변경 모달 — 일반 액션 */}
      {editing && (
        <PriceEditModal
          price={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {/* 변경 모달 — 원화 환산 + 환원 (USE_TICKET_OFFLINE) */}
      {editingKrwRatio && (
        <KrwRatioEditModal
          ratioPolicy={krwRatioPolicies.ratio}
          minPolicy={krwRatioPolicies.min}
          krwPerCreditPolicy={krwRatioPolicies.krwPerCredit}
          reversalRatioPolicy={krwRatioPolicies.reversalRatio}
          reversalWindowPolicy={krwRatioPolicies.reversalWindow}
          krwFloorPolicy={krwRatioPolicies.krwFloor}
          floorCreditsPolicy={krwRatioPolicies.floorCredits}
          onClose={() => setEditingKrwRatio(null)}
          onSaved={() => {
            setEditingKrwRatio(null);
            load();
          }}
        />
      )}

      {/* 변경 모달 — 알림톡 (USE_KAKAO_ALIM + 대체문자 USE_SMS_FALLBACK 통합) */}
      {editingKakaoAlim && (
        <KakaoAlimEditModal
          alimPrice={editingKakaoAlim}
          smsFallbackPrice={smsFallbackPrice}
          onClose={() => setEditingKakaoAlim(null)}
          onSaved={() => {
            setEditingKakaoAlim(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  페이징 컨트롤 — CreditCompaniesPage 와 동일한 패턴
 * ───────────────────────────────────────────────────────────────── */
function PricePagination({
  page, totalPages, onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const pages: (number | '...')[] = (() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const out: (number | '...')[] = [1];
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) out.push('...');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < totalPages - 1) out.push('...');
    out.push(totalPages);
    return out;
  })();
  const btn = 'inline-flex h-7 min-w-[28px] items-center justify-center rounded text-xs transition-colors';

  return (
    <div className="flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className={`${btn} px-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <ChevronLeft size={14} />
      </button>
      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`dot-${i}`} className="px-1 text-xs text-gray-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p)}
            className={`${btn} px-2 ${
              p === page
                ? 'bg-primary-500 text-white font-semibold'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className={`${btn} px-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent`}
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  금액 변경 모달 — 새 금액 + 시행일 + 사유 (일반 액션)
 * ───────────────────────────────────────────────────────────────── */
function PriceEditModal({
  price,
  onClose,
  onSaved,
}: {
  price: CreditPrice;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [newPrice, setNewPrice] = useState(price.unitPrice);
  const [newName, setNewName] = useState(price.description ?? '');
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const trimmedName = newName.trim();
  const prevName = (price.description ?? '').trim();
  const nameChanged = trimmedName !== prevName;

  // 사유는 선택 — 입력 시에만 5자+ 검증
  const reasonTrimmed = reason.trim();
  const reasonInvalid = reasonTrimmed.length > 0 && reasonTrimmed.length < 5;

  // 저장 조건 — 변경 사항 게이팅 제거. 금액/액션명만 비어있지 않으면 항상 저장 가능.
  const canSave =
    newPrice > 0 &&
    trimmedName.length > 0 &&
    !reasonInvalid &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await creditApi.updatePrice(
        price.priceSeq,
        {
          unitPrice: newPrice,
          // 항상 액션명 전송 — 비어있지 않으면 BE 가 갱신, 빈문자열이면 기존 값 유지
          description: trimmedName,
          dtEffectiveFrom: new Date(effectiveDate).toISOString(),
        },
        reasonTrimmed || undefined  // 빈 사유는 BE 가 fallback 처리
      );
      onSaved();
    } catch (e) {
      alert('저장 실패: ' + (e instanceof Error ? e.message : String(e)));
      setSaving(false);
    }
  };

  const diff = newPrice - price.unitPrice;

  return (
    <Modal isOpen onClose={onClose} title="금액 변경" size="md">
      <div className="space-y-4">
        {/* 대상 */}
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs text-gray-500">변경 대상</div>
          <div className="mt-0.5 font-semibold text-gray-900">
            {price.description?.trim() || CREDIT_TX_LABEL[price.cdTxType] || price.cdTxType}
          </div>
          <code className="text-xs text-gray-400">{price.cdTxType}</code>
        </div>

        {/* 액션 명 */}
        <div>
          <label className="text-xs font-medium text-gray-700">
            액션 명 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="예: AI 알림장 (텍스트), SMS 발송 등"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          {nameChanged && (
            <p className="mt-1 text-xs text-amber-700">
              ✎ "{prevName}" → "{trimmedName}" 로 변경됩니다.
            </p>
          )}
        </div>

        {/* 현재 → 새 금액 */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700">현재 금액</label>
            <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-lg font-bold text-gray-900">
              {price.unitPrice} <span className="text-sm text-gray-500">크레딧</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">새 금액</label>
            <input
              type="number"
              min={0}
              value={newPrice}
              onChange={(e) => setNewPrice(parseInt(e.target.value) || 0)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-lg font-bold focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* 변화량 */}
        {diff !== 0 && (
          <div
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              diff > 0
                ? 'bg-red-50 text-red-700'
                : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {diff > 0 ? '▲' : '▼'} {Math.abs(diff)} 크레딧 ({diff > 0 ? '+' : ''}
            {((diff / price.unitPrice) * 100).toFixed(0)}%) {diff > 0 ? '인상' : '인하'}
          </div>
        )}

        {/* 시행일 */}
        <div>
          <label className="text-xs font-medium text-gray-700">시행일</label>
          <input
            type="date"
            value={effectiveDate}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            지정한 날 00:00 이후 발생한 차감부터 새 금액 적용. 그 이전은 현재 금액 유지.
          </p>
        </div>

        {/* 사유 (선택) */}
        <div>
          <label className="text-xs font-medium text-gray-700">변경 사유 (선택)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 결제 시스템 사용률 5% → 10% 목표 상향 (선택)"
            className={`mt-1 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 ${
              reasonInvalid ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-primary-500'
            }`}
          />
          <p className={`mt-1 text-xs ${reasonInvalid ? 'text-red-600' : 'text-gray-500'}`}>
            {reasonInvalid
              ? '입력 시 5자 이상이어야 합니다 (또는 비워두세요).'
              : '비워두면 "(사유 미입력)" 으로 감사 로그에 기록됩니다.'}
          </p>
        </div>

        {/* 액션 */}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            변경 적용
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  KrwRatioEditModal — USE_TICKET_OFFLINE 전용 (원화 환산 정책)
 *
 *  일반 액션은 "건당 N크레딧" 단가지만, 이용권 수동발급은 가격이 천차만별이라
 *  이용권 원화 × 비율 ÷ 1크레딧 단가 로 환산. 무료(0%) 정책이어도 최소 부과.
 *
 *  편집 대상:
 *   - MANUAL_TICKET_KRW_RATIO   (환산 비율 %)
 *   - MANUAL_TICKET_MIN_CREDITS (최소 차감 크레딧)
 *
 *  KRW_PER_CREDIT 은 표시만 (변경은 조건 설정 페이지에서).
 * ───────────────────────────────────────────────────────────────── */
function KrwRatioEditModal({
  ratioPolicy,
  minPolicy,
  krwPerCreditPolicy,
  reversalRatioPolicy,
  reversalWindowPolicy,
  krwFloorPolicy,
  floorCreditsPolicy,
  onClose,
  onSaved,
}: {
  ratioPolicy?: CreditPolicy;
  minPolicy?: CreditPolicy;
  krwPerCreditPolicy?: CreditPolicy;
  reversalRatioPolicy?: CreditPolicy;
  reversalWindowPolicy?: CreditPolicy;
  krwFloorPolicy?: CreditPolicy;
  floorCreditsPolicy?: CreditPolicy;
  onClose: () => void;
  onSaved: () => void;
}) {
  // 발급 정책 (차감)
  const [ratio, setRatio] = useState<string>(ratioPolicy?.policyValue ?? '0.9');
  const [minCredits, setMinCredits] = useState<string>(minPolicy?.policyValue ?? '1');
  // FLOOR 정책 — 최소 금액 미만 시 일괄 크레딧
  const [krwFloor, setKrwFloor] = useState<string>(krwFloorPolicy?.policyValue ?? '5000');
  const [floorCredits, setFloorCredits] = useState<string>(floorCreditsPolicy?.policyValue ?? '10');
  // 취소 정책 (환원)
  const [reversalRatio, setReversalRatio] = useState<string>(reversalRatioPolicy?.policyValue ?? '100');
  const [reversalWindow, setReversalWindow] = useState<string>(reversalWindowPolicy?.policyValue ?? '30');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // 시뮬레이션 입력
  const [simKrw, setSimKrw] = useState<number>(300000);
  const [simQty, setSimQty] = useState<number>(1);
  const [simDaysAfter, setSimDaysAfter] = useState<number>(5); // 발급 후 N일 뒤 취소

  const krwPerCredit = parseFloat(krwPerCreditPolicy?.policyValue ?? '6.5') || 6.5;
  const ratioNum = parseFloat(ratio.trim());
  const minNum = parseInt(minCredits.trim(), 10);
  const revRatioNum = parseInt(reversalRatio.trim(), 10);
  const revWindowNum = parseInt(reversalWindow.trim(), 10);
  const krwFloorNum = parseInt(krwFloor.trim(), 10);
  const floorCreditsNum = parseInt(floorCredits.trim(), 10);
  const validRatio = !Number.isNaN(ratioNum) && ratioNum >= 0 && ratioNum <= 100;
  const validMin = !Number.isNaN(minNum) && minNum >= 0 && minNum <= 1_000_000;
  const validRevRatio = !Number.isNaN(revRatioNum) && revRatioNum >= 0 && revRatioNum <= 100;
  const validRevWindow = !Number.isNaN(revWindowNum) && revWindowNum >= 0 && revWindowNum <= 3650;
  const validKrwFloor = !Number.isNaN(krwFloorNum) && krwFloorNum >= 0 && krwFloorNum <= 10_000_000;
  const validFloorCredits = !Number.isNaN(floorCreditsNum) && floorCreditsNum >= 0 && floorCreditsNum <= 1_000_000;

  const ratioChanged = ratioPolicy && ratio.trim() !== ratioPolicy.policyValue;
  const minChanged = minPolicy && minCredits.trim() !== minPolicy.policyValue;
  const revRatioChanged = reversalRatioPolicy && reversalRatio.trim() !== reversalRatioPolicy.policyValue;
  const revWindowChanged = reversalWindowPolicy && reversalWindow.trim() !== reversalWindowPolicy.policyValue;
  const krwFloorChanged = krwFloorPolicy && krwFloor.trim() !== krwFloorPolicy.policyValue;
  const floorCreditsChanged = floorCreditsPolicy && floorCredits.trim() !== floorCreditsPolicy.policyValue;
  const anyChanged = !!(ratioChanged || minChanged || revRatioChanged || revWindowChanged || krwFloorChanged || floorCreditsChanged);
  const reasonOk = reason.trim().length >= 5;
  const allValid = validRatio && validMin && validRevRatio && validRevWindow && validKrwFloor && validFloorCredits;
  const canSave = allValid && anyChanged && reasonOk && !saving;

  // 시뮬레이션 계산 — 발급(차감) → 취소(환원) 사이클 + 온라인 PG 비교
  const ONLINE_PG_RATE = 4;
  const sim = useMemo(() => {
    if (!allValid || krwPerCredit <= 0) return null;
    const krwBase = simKrw * Math.max(simQty, 1);
    // 발급 차감
    const krwTotal = (krwBase * ratioNum) / 100;
    const raw = Math.floor(krwTotal / krwPerCredit);
    // FLOOR 정책 우선 — 총 원가가 KRW_FLOOR 미만이면 무조건 FLOOR_CREDITS 부과
    const floorApplied = krwBase < krwFloorNum;
    const debited = floorApplied ? floorCreditsNum : Math.max(raw, Math.max(minNum, 0));
    // 온라인 PG 비교
    const onlineFeeKrw = (krwBase * ONLINE_PG_RATE) / 100;
    const diff = onlineFeeKrw - krwTotal;
    // 환원 (취소 시)
    const withinWindow = revWindowNum === 0 || simDaysAfter <= revWindowNum; // 0=무기한
    const reversed = withinWindow ? Math.floor((debited * revRatioNum) / 100) : 0;
    const netCredits = debited - reversed; // 사장님 순 부담
    return {
      krwBase, krwTotal, raw, debited,
      applied: floorApplied ? 'floor' as const : (debited > raw ? 'minimum' as const : 'raw' as const),
      floorApplied,
      onlineFeeKrw, diff,
      withinWindow, reversed, netCredits,
    };
  }, [simKrw, simQty, simDaysAfter, ratioNum, minNum, revRatioNum, revWindowNum, krwFloorNum, floorCreditsNum, krwPerCredit, allValid]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const reasonText = reason.trim();
      const tasks: Promise<unknown>[] = [];
      if (ratioPolicy && ratioChanged) {
        tasks.push(creditApi.updatePolicy(ratioPolicy.policyKey, ratio.trim(), reasonText));
      }
      if (minPolicy && minChanged) {
        tasks.push(creditApi.updatePolicy(minPolicy.policyKey, minCredits.trim(), reasonText));
      }
      if (reversalRatioPolicy && revRatioChanged) {
        tasks.push(creditApi.updatePolicy(reversalRatioPolicy.policyKey, reversalRatio.trim(), reasonText));
      }
      if (reversalWindowPolicy && revWindowChanged) {
        tasks.push(creditApi.updatePolicy(reversalWindowPolicy.policyKey, reversalWindow.trim(), reasonText));
      }
      if (krwFloorPolicy && krwFloorChanged) {
        tasks.push(creditApi.updatePolicy(krwFloorPolicy.policyKey, krwFloor.trim(), reasonText));
      }
      if (floorCreditsPolicy && floorCreditsChanged) {
        tasks.push(creditApi.updatePolicy(floorCreditsPolicy.policyKey, floorCredits.trim(), reasonText));
      }
      await Promise.all(tasks);
      onSaved();
    } catch (e) {
      console.error('원화 환산 정책 변경 실패', e);
      alert('정책 변경에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="원화 환산 — 이용권 오프라인 수동발급" size="xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* ── 좌측 — 정책 입력 (차감 + 환원) ───────── */}
        <div className="space-y-3">
          {/* 안내 */}
          <div className="bg-sky-50 border border-sky-200 rounded px-2.5 py-2 text-[11px] text-sky-900 leading-snug">
            <div className="font-mono text-[11px] text-sky-800">
              FLOOR(금액 × 수량 × 비율% ÷ ₩{krwPerCredit})
            </div>
            <div className="mt-1 text-[10px] text-sky-700">
              권장: 차감 <b>0.9%</b> (PG {ONLINE_PG_RATE}% 대비) · 환원 <b>100%</b> · 환원 가능 <b>30일</b>
            </div>
          </div>

          {/* 발급 정책 (차감) */}
          <div className="border border-sky-200 rounded-lg p-2.5 bg-sky-50/40">
            <div className="text-[11px] font-bold text-sky-800 mb-2">
              📤 발급 시 차감
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                  환산 비율 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" step="0.1" min={0} max={100}
                    value={ratio}
                    onChange={(e) => setRatio(e.target.value)}
                    className={`w-full pr-6 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                      validRatio ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                    }`}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">%</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                  최소 크레딧 <span className="text-red-500">*</span>
                  <span className="ml-1 text-[9px] text-gray-400">(deprecated)</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={0}
                    value={minCredits}
                    onChange={(e) => setMinCredits(e.target.value)}
                    className={`w-full pr-6 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                      validMin ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                    }`}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">크</span>
                </div>
              </div>
            </div>

            {/* FLOOR 정책 — 최소 금액 미만 시 일괄 크레딧 (신규) */}
            <div className="mt-2 border-t border-sky-200 pt-2">
              <div className="text-[10px] font-semibold text-sky-800 mb-1">
                💡 최소 부과 정책 — <span className="text-sky-600">총 금액이 기준 미만이면 일괄 부과</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                    최소 금액 기준 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number" min={0} step={100}
                      value={krwFloor}
                      onChange={(e) => setKrwFloor(e.target.value)}
                      className={`w-full pr-6 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                        validKrwFloor ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                      }`}
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">원</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                    일괄 부과 크레딧 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number" min={0}
                      value={floorCredits}
                      onChange={(e) => setFloorCredits(e.target.value)}
                      className={`w-full pr-6 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                        validFloorCredits ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                      }`}
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">크</span>
                  </div>
                </div>
              </div>
              <div className="mt-1 text-[9px] text-sky-700">
                예) 총 금액이 <b>{krwFloor}</b>원 미만이면 → 계산 무시하고 <b>{floorCredits}크</b> 부과
              </div>
            </div>
          </div>

          {/* 취소 정책 (환원) */}
          <div className="border border-emerald-200 rounded-lg p-2.5 bg-emerald-50/40">
            <div className="text-[11px] font-bold text-emerald-800 mb-2">
              📥 취소 시 환원 (차감 크레딧 복원)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                  환원 비율 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={0} max={100}
                    value={reversalRatio}
                    onChange={(e) => setReversalRatio(e.target.value)}
                    className={`w-full pr-6 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                      validRevRatio ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                    }`}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">%</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-700 mb-0.5">
                  환원 가능 기간 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number" min={0} max={3650}
                    value={reversalWindow}
                    onChange={(e) => setReversalWindow(e.target.value)}
                    className={`w-full pr-7 rounded border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-1 ${
                      validRevWindow ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500' : 'border-red-400'
                    }`}
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-500">일</span>
                </div>
              </div>
            </div>
            <p className="mt-1.5 text-[10px] text-emerald-700">
              100=전액 환원 · 0=환원 없음 · 환원기간 0=무기한
            </p>
          </div>

          {/* 변경 사유 */}
          <div>
            <label className="block text-[11px] font-bold text-gray-700 mb-1">
              변경 사유 <span className="text-red-500">*</span>
              <span className="text-gray-500 font-normal ml-1">(5자 이상)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="예: 운영 정책 회의 결과 환원 가능 기간을 60일로 연장"
              className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:outline-none focus:border-primary-500 resize-none"
            />
          </div>
        </div>

        {/* ── 우측 — 시뮬레이션 (한 사이클) ────────── */}
        <div className="space-y-2.5">
          <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
            <div className="text-[11px] font-bold text-gray-700 mb-2 flex items-center gap-1.5">
              <Calculator size={12} className="text-sky-600" /> 시뮬레이션 — 발급 → 취소 사이클
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[11px]">
              <span className="text-gray-600">이용권</span>
              <input
                type="number" min={0}
                value={simKrw}
                onChange={(e) => setSimKrw(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-24 rounded border border-gray-300 px-1.5 py-1 text-xs text-right"
              />
              <span className="text-gray-500">원 ×</span>
              <input
                type="number" min={1}
                value={simQty}
                onChange={(e) => setSimQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-12 rounded border border-gray-300 px-1.5 py-1 text-xs text-right"
              />
              <span className="text-gray-500">매 · 취소 시점</span>
              <input
                type="number" min={0}
                value={simDaysAfter}
                onChange={(e) => setSimDaysAfter(Math.max(0, parseInt(e.target.value) || 0))}
                className="w-12 rounded border border-gray-300 px-1.5 py-1 text-xs text-right"
              />
              <span className="text-gray-500">일 후</span>
            </div>

            {sim && (
              <div className="space-y-2">
                {/* ① 발급 */}
                <div className="bg-sky-50 border border-sky-200 rounded p-2 text-[10px] font-mono">
                  <div className="text-sky-800 font-bold mb-0.5">① 발급 시 (차감)</div>
                  <div className="text-gray-700">
                    ₩{simKrw.toLocaleString()} × {simQty} × {ratioNum}% = ₩{Math.round(sim.krwTotal).toLocaleString()}
                  </div>
                  <div className="text-gray-700">
                    ÷ ₩{krwPerCredit} = <b>{sim.raw.toLocaleString()}</b> 크
                    {sim.applied === 'minimum' && (
                      <span className="text-amber-700"> → 최소 {minNum}크 적용</span>
                    )}
                  </div>
                  <div className="border-t border-sky-100 mt-1 pt-1 text-xs text-sky-700">
                    <b>-{sim.debited.toLocaleString()} 크</b> 차감
                  </div>
                </div>

                {/* ② 취소 → 환원 */}
                <div className={`border rounded p-2 text-[10px] font-mono ${
                  sim.reversed > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-100 border-gray-300'
                }`}>
                  <div className={`font-bold mb-0.5 ${sim.reversed > 0 ? 'text-emerald-800' : 'text-gray-600'}`}>
                    ② 취소 시 (환원) — 발급 {simDaysAfter}일 후
                  </div>
                  {sim.withinWindow ? (
                    <>
                      <div className="text-gray-700">
                        환원 가능 ✓ (기간 {revWindowNum === 0 ? '무기한' : `${revWindowNum}일`} 이내)
                      </div>
                      <div className="text-gray-700">
                        {sim.debited.toLocaleString()}크 × {revRatioNum}% = <b>{sim.reversed.toLocaleString()}</b> 크 환원
                      </div>
                    </>
                  ) : (
                    <div className="text-red-700">
                      환원 불가 ✗ (기간 {revWindowNum}일 초과 — {simDaysAfter}일 후 취소)
                    </div>
                  )}
                  <div className={`border-t mt-1 pt-1 text-xs ${
                    sim.reversed > 0 ? 'border-emerald-100 text-emerald-700' : 'border-gray-200 text-gray-500'
                  }`}>
                    <b>+{sim.reversed.toLocaleString()} 크</b> 환원
                  </div>
                </div>

                {/* 순 결과 */}
                <div className="bg-white border-2 border-gray-300 rounded p-2 text-center">
                  <div className="text-[10px] text-gray-500">사장님 순 부담</div>
                  <div className="text-lg font-bold text-gray-900">
                    {sim.netCredits.toLocaleString()} 크
                  </div>
                  <div className="text-[10px] text-gray-500">
                    ≈ ₩{Math.round(sim.netCredits * krwPerCredit).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 온/오프 비교 */}
          {sim && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[10px] text-amber-900">
              <div className="font-bold mb-1 text-[10px]">📊 PG ({ONLINE_PG_RATE}%) vs 차감 ({ratioNum}%)</div>
              <div className="grid grid-cols-2 gap-1.5 font-mono">
                <div className="bg-white rounded px-1.5 py-1 border border-amber-100">
                  <div className="text-[9px] text-gray-500">PG 수수료</div>
                  <div className="text-xs font-bold text-gray-900">
                    ₩{Math.round(sim.onlineFeeKrw).toLocaleString()}
                  </div>
                </div>
                <div className="bg-white rounded px-1.5 py-1 border border-amber-100">
                  <div className="text-[9px] text-gray-500">오프라인</div>
                  <div className="text-xs font-bold text-sky-700">
                    ₩{Math.round(sim.krwTotal).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          ⚠ 즉시 적용 · 변경된 정책마다 이력 자동 기록
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            변경 적용
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  KakaoAlimEditModal — USE_KAKAO_ALIM + 대체문자(USE_SMS_FALLBACK) 통합 편집
 *
 *  두 액션을 한 화면에서 함께 관리:
 *   - 알림톡 단가       : USE_KAKAO_ALIM 크레딧/건
 *   - 대체문자 단가     : USE_SMS_FALLBACK 크레딧/건 (알림톡 실패 → SMS 대체 시)
 *
 *  운영자 관점: 알림톡 서비스 하나의 단가 세트로 인식.
 * ───────────────────────────────────────────────────────────────── */
function KakaoAlimEditModal({
  alimPrice,
  smsFallbackPrice,
  onClose,
  onSaved,
}: {
  alimPrice: CreditPrice;
  smsFallbackPrice?: CreditPrice;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [alimUnit, setAlimUnit] = useState<string>(String(alimPrice.unitPrice));
  const [smsUnit, setSmsUnit] = useState<string>(String(smsFallbackPrice?.unitPrice ?? 2));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const alimNum = parseInt(alimUnit.trim(), 10);
  const smsNum = parseInt(smsUnit.trim(), 10);
  const validAlim = !Number.isNaN(alimNum) && alimNum >= 0 && alimNum <= 100_000;
  const validSms = !Number.isNaN(smsNum) && smsNum >= 0 && smsNum <= 100_000;

  const alimChanged = alimUnit.trim() !== String(alimPrice.unitPrice);
  const smsChanged = smsFallbackPrice
    ? smsUnit.trim() !== String(smsFallbackPrice.unitPrice)
    : smsUnit.trim() !== '2';   // 없으면 기본 2와 다를 때 저장 대상

  const anyChanged = alimChanged || smsChanged;
  const reasonOk = reason.trim().length >= 5;
  const canSave = validAlim && validSms && anyChanged && reasonOk && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const reasonText = reason.trim();
      const tasks: Promise<unknown>[] = [];
      if (alimChanged) {
        tasks.push(
          creditApi.updatePrice(
            alimPrice.priceSeq,
            { unitPrice: alimNum },
            reasonText
          )
        );
      }
      if (smsChanged) {
        if (smsFallbackPrice) {
          tasks.push(
            creditApi.updatePrice(
              smsFallbackPrice.priceSeq,
              { unitPrice: smsNum },
              reasonText
            )
          );
        } else {
          // 최초 등록
          tasks.push(
            creditApi.createPrice(
              {
                cdTxType: 'USE_SMS_FALLBACK',
                unitPrice: smsNum,
                description: '알림톡 실패 시 대체 SMS 발송 단가 (건당)',
              },
              reasonText
            )
          );
        }
      }
      await Promise.all(tasks);
      onSaved();
    } catch (e) {
      console.error('알림톡 단가 변경 실패', e);
      alert('단가 변경에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="알림톡 발송 서비스 금액" size="md">
      <div className="space-y-4 p-1">
        {/* 안내 */}
        <div className="bg-sky-50 border border-sky-200 rounded px-3 py-2 text-[12px] text-sky-900 leading-snug">
          카카오 알림톡과 대체문자(SMS)를 한 화면에서 함께 관리합니다.
          <br />
          알림톡 실패 시 자동으로 SMS로 대체 발송되며, 이때 <b>대체문자 단가</b>로 차감됩니다.
        </div>

        {/* 알림톡 단가 */}
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <div className="text-[12px] font-bold text-gray-800 mb-2">
            📨 카카오 알림톡 (건당)
          </div>
          <div className="relative w-40">
            <input
              type="number"
              min={0}
              value={alimUnit}
              onChange={(e) => setAlimUnit(e.target.value)}
              className={`w-full pr-8 rounded border px-2 py-1.5 text-sm font-bold focus:outline-none focus:ring-1 ${
                validAlim
                  ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  : 'border-red-400'
              }`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
              크레딧
            </span>
          </div>
        </div>

        {/* 대체문자 단가 */}
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <div className="text-[12px] font-bold text-gray-800 mb-1">
            💬 대체문자 (SMS) — 알림톡 실패 시 자동 발송 (건당)
          </div>
          <div className="text-[11px] text-gray-500 mb-2">
            카카오톡 미설치·차단 등으로 알림톡이 도달하지 못한 경우 SMS로 대체 발송됩니다.
          </div>
          <div className="relative w-40">
            <input
              type="number"
              min={0}
              value={smsUnit}
              onChange={(e) => setSmsUnit(e.target.value)}
              className={`w-full pr-8 rounded border px-2 py-1.5 text-sm font-bold focus:outline-none focus:ring-1 ${
                validSms
                  ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  : 'border-red-400'
              }`}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
              크레딧
            </span>
          </div>
        </div>

        {/* 사유 */}
        <div>
          <label className="block text-[12px] font-semibold text-gray-700 mb-1">
            변경 사유 <span className="text-red-500">*</span>
            <span className="ml-1 text-[10px] text-gray-400">(5자 이상)</span>
          </label>
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="변경 사유를 입력하세요 (예: 알리고 단가 인상 반영)"
            className={`w-full rounded border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 ${
              reasonOk
                ? 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                : 'border-red-300'
            }`}
          />
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-gray-200 mt-4 pt-3">
        <div className="text-[11px] text-gray-500">
          ⚠ 즉시 적용 · 변경 이력 자동 기록
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
            변경 적용
          </Button>
        </div>
      </div>
    </Modal>
  );
}
